import { useState, useEffect } from "react";
import { DateField } from "@/components/ui/date-field";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fmtEur, fmtDate, photoSrc } from "@/lib/format";
import InvoiceThumb from "@/components/InvoiceThumb";
import { Wallet, ChevronDown, ChevronRight, CheckCircle2, FileText } from "lucide-react";

export default function Payouts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState({});
  const [showDone, setShowDone] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => api.get("/factoring").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleDone = async (f, done) => {
    try { await api.patch(`/factoring/${f.id}/payout`, { done }); toast.success(done ? "Uitbetaling afgevinkt" : "Teruggezet naar openstaand"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bijwerken mislukt"); }
  };

  const inRange = (r) => {
    if (!r.payout_done) return true;
    const d = (r.payout_done_at || r.payout_date || "").slice(0, 10);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };
  const visible = rows.filter((r) => (showDone || !r.payout_done) && inRange(r));
  // groepeer per uitbetaaldatum (of factuurdatum als fallback)
  const groups = {};
  visible.forEach((r) => { const d = r.payout_date || r.invoice_date || "onbekend"; (groups[d] = groups[d] || []).push(r); });
  const dates = Object.keys(groups).sort((a, b) => (a < b ? 1 : -1));

  const openTotal = rows.filter((r) => !r.payout_done).reduce((s, r) => s + (r.payout || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Uitbetalingen</h1>
          <p className="text-slate-500">Per dag klaargezette uitbetalingen — vink handmatig af zodra uitgevoerd.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="payouts-show-done-toggle">
          <Checkbox checked={showDone} onCheckedChange={(v) => setShowDone(!!v)} /> Toon afgevinkte
        </label>
      </div>

      {showDone && (
        <Card className="p-3 flex flex-wrap items-end gap-3" data-testid="payouts-daterange">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Afgevinkt van</label>
            <DateField testId="payouts-from" value={from} onChange={setFrom} className="!w-40 h-9" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">t/m</label>
            <DateField testId="payouts-to" value={to} onChange={setTo} className="!w-40 h-9" />
          </div>
          {(from || to) && <button className="text-xs text-slate-500 underline h-9" onClick={() => { setFrom(""); setTo(""); }} data-testid="payouts-clear-range">Wissen</button>}
          <span className="text-xs text-slate-400">Filtert afgevinkte uitbetalingen op datum van uitvoering.</span>
        </Card>
      )}

      <Card className="p-4 flex items-center gap-3 bg-emerald-50 border-emerald-200">
        <Wallet className="h-6 w-6 text-emerald-600" />
        <div>
          <div className="text-xs text-slate-500">Totaal nog uit te betalen</div>
          <div className="text-2xl font-bold text-emerald-700" data-testid="payouts-open-total">{fmtEur(openTotal)}</div>
        </div>
      </Card>

      {dates.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Geen uitbetalingen klaargezet.</p>}

      {dates.map((d) => (
        <div key={d} className="space-y-2" data-testid={`payout-group-${d}`}>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            {d === "onbekend" ? "Onbekende datum" : fmtDate(d)}
            <Badge variant="secondary">{groups[d].length}</Badge>
          </div>
          <Card className="divide-y">
            {groups[d].map((f) => {
              const fee = (f.amount || 0) * (f.interest_rate || 0) / 100;
              const isOpen = !!open[f.id];
              return (
                <div key={f.id} data-testid={`payout-row-${f.id}`}>
                  <div className="flex items-center gap-3 p-3">
                    <button onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))} className="text-slate-400 shrink-0" data-testid={`payout-expand-${f.id}`}>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}>
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        {f.debtor_name || "—"}
                        {f.payout_done && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Uitbetaald</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">{f.invoice_number ? `Factuur ${f.invoice_number} • ` : ""}Factuurbedrag {fmtEur(f.amount)} (te ontvangen) • Uit te keren {fmtEur(f.payout)}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-emerald-700">{fmtEur(f.payout)}</div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer shrink-0 pl-2">
                      <Checkbox checked={!!f.payout_done} onCheckedChange={(v) => toggleDone(f, !!v)} data-testid={`payout-check-${f.id}`} /> Uitgevoerd
                    </label>
                  </div>
                  {isOpen && (
                    <div className="px-11 pb-3 grid sm:grid-cols-3 gap-3 text-sm bg-slate-50/60" data-testid={`payout-detail-${f.id}`}>
                      <div><div className="text-xs text-slate-400">Begunstigde (klant)</div><div className="font-medium text-slate-800">{f.debtor_name || "—"}</div></div>
                      <div><div className="text-xs text-slate-400">Origineel factuurbedrag (te ontvangen)</div><div className="font-medium text-slate-800" data-testid={`payout-invoice-amount-${f.id}`}>{fmtEur(f.amount)}</div></div>
                      <div><div className="text-xs text-slate-400">Fee ({f.interest_rate || 0}%)</div><div className="font-medium text-red-600">-{fmtEur(fee)}</div></div>
                      <div><div className="text-xs text-slate-400">Uit te keren aan klant</div><div className="font-medium text-emerald-700">{fmtEur(f.payout)}</div></div>
                      <div><div className="text-xs text-slate-400">IBAN</div><div className="font-mono text-slate-800">{f.iban || "—"}</div></div>
                      <div><div className="text-xs text-slate-400">Kopie-factuur</div><InvoiceThumb url={f.file_url} label="Bekijk factuur" testid={`payout-invoice-${f.id}`} /></div>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      ))}
    </div>
  );
}
